import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import PasswordInput from "../components/PasswordInput";
import { ExpiredAccounts, ConfirmDialog } from "../components/ExpiredAccounts";
import { ManualActivateDialog } from "../components/ManualActivateDialog";
import { AdminBenefits } from "../components/AdminBenefits";
import { AdminConsumptionPanel } from "../components/ConsumptionBalance";
import { AdminOrders } from "../components/AdminOrders";
import { AdminWithdrawals } from "../components/AdminWithdrawals";
import { AdminTransfers } from "../components/AdminTransfers";
import { AdminPartnerCompanies } from "../components/AdminPartnerCompanies";
import { AdminClosings } from "../components/AdminClosings";
import { Users, TrendingUp, Wallet, Sparkles, Search, Pencil, FileText, ShieldCheck, Trash2, KeyRound, LogIn } from "lucide-react";

const PLAN_OPTS = [
  { v: "plan_associado", l: "Associado Ativo" },
];

const EDIT_PLAN_OPTS = [
  { v: "plan_associado", l: "Associado Ativo — R$ 39,90" },
];

const Metric = ({ icon: Icon, label, value, sub, testid }) => (
  <Card className="p-6 gold-border" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-foreground">
      <Icon className="h-4 w-4" /> {label}
    </div>
    <div className="mt-2 font-mono text-3xl font-bold gold-text">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </Card>
);

export default function Admin() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [stats, setStats] = useState(null);
  const [plansCfg, setPlansCfg] = useState([]);
  const [settings, setSettings] = useState({ cleanup_30d_enabled: false });
  const [ledger, setLedger] = useState([]);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("all");
  const [status, setStatus] = useState("all");
  const [accountType, setAccountType] = useState("associate");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // user object being edited
  const [form, setForm] = useState({ plan: "plan_associado", status: "active", role: "user" });
  const [proof, setProof] = useState(null); // { user, filename, content_type, data_b64 }
  const [manualUser, setManualUser] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdLoading, setPwdLoading] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (plan !== "all") params.plan = plan;
      if (status !== "all") params.status = status;
      params.account_type = accountType;
      const { data } = await api.get("/admin/users", { params });
      setUsers(data.items);
      setTotal(data.total);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q, plan, status, accountType]);

  useEffect(() => {
    loadStats(); loadUsers();
    api.get("/config").then((r) => setPlansCfg(r.data.plans || [])).catch(() => {});
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/ledger", { params: { limit: 30 } }).then((r) => setLedger(r.data)).catch(() => {});
  }, [loadStats, loadUsers]);

  const toggleCleanup = async () => {
    const next = !settings.cleanup_30d_enabled;
    try {
      const { data } = await api.patch("/admin/settings", { cleanup_30d_enabled: next });
      setSettings((s) => ({ ...s, ...data }));
      toast.success(next ? "Regra 20d ativada" : "Regra 20d desativada");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (u) => {
    setEditing(u);
    const validPlan = EDIT_PLAN_OPTS.some((p) => p.v === u.plan) ? u.plan : "";
    setForm({ plan: validPlan, status: u.status, role: u.role });
  };

  const save = async () => {
    if (!editing) return;
    const changes = Object.fromEntries(
      Object.entries(form).filter(([k, v]) => v && v !== editing[k])
    );
    if (!Object.keys(changes).length) { setEditing(null); return; }
    try {
      await api.patch(`/admin/users/${editing.id}`, changes);
      toast.success("Usuário atualizado");
      setEditing(null);
      loadUsers(); loadStats();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteUser = async () => {
    try {
      await api.delete(`/admin/users/${editing.id}`);
      toast.success("Usuário excluído");
      setDeleteOpen(false);
      setEditing(null);
      loadUsers(); loadStats();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleStatus = async (u) => {
    const next = u.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/admin/users/${u.id}`, { status: next });
      toast.success(next === "active" ? "Usuário ativado" : "Usuário desativado");
      loadUsers(); loadStats();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const viewProof = async (u) => {
    try {
      const { data } = await api.get(`/admin/users/${u.id}/proof`);
      setProof({ user: u, ...data });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const activateUser = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/activate`);
      toast.success("Cadastro ativado");
      setProof(null);
      loadUsers(); loadStats();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const impersonate = async (u) => {
    if (!window.confirm(`Entrar como "${u.name}"? Você poderá voltar clicando em "Voltar como Admin".`)) return;
    try {
      await api.post(`/admin/users/${u.id}/impersonate`);
      await refresh();
      toast.success(`Agora você está como ${u.name}`);
      navigate("/dashboard");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doReset = async () => {
    if (resetConfirm !== "ZERAR") {
      toast.error('Digite "ZERAR" para confirmar');
      return;
    }
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-test-data", { confirm: "ZERAR" });
      toast.success(`Removidos ${data.users_deleted} usuários e ${data.ledger_deleted} lançamentos`);
      setResetOpen(false);
      setResetConfirm("");
      loadUsers(); loadStats();
      api.get("/admin/ledger", { params: { limit: 30 } }).then((r) => setLedger(r.data)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResetting(false); }
  };

  const changePassword = async () => {    if (pwd.new_password.length < 6) return toast.error("A nova senha deve ter pelo menos 6 caracteres");
    if (pwd.new_password !== pwd.confirm) return toast.error("As senhas não coincidem");
    setPwdLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success("Senha alterada com sucesso");
      setPwdOpen(false);
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPwdLoading(false); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Painel Administrativo</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">
          Controle da <span className="gold-text">Conecta Mais</span>
        </h1>

        <Tabs defaultValue="dashboard" className="mt-8">
          <TabsList data-testid="admin-tabs" className="flex-wrap h-auto justify-start">
            <TabsTrigger value="dashboard" data-testid="admin-tab-dashboard">Métricas</TabsTrigger>
            <TabsTrigger value="movements" data-testid="admin-tab-movements">Movimentações</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Usuários</TabsTrigger>
            <TabsTrigger value="closings" data-testid="admin-tab-closings">Compressão</TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders">Pedidos</TabsTrigger>
            <TabsTrigger value="withdrawals" data-testid="admin-tab-withdrawals">Saques</TabsTrigger>
            <TabsTrigger value="transfers" data-testid="admin-tab-transfers">Transferências</TabsTrigger>
            <TabsTrigger value="partners" data-testid="admin-tab-partners">Parceiros</TabsTrigger>
            <TabsTrigger value="benefits" data-testid="admin-tab-benefits">Benefícios</TabsTrigger>
            <TabsTrigger value="expired" data-testid="admin-tab-expired">Vencidas</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings">Configurações</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-6">
            {!stats ? (
              <p className="text-sm text-muted-foreground">Carregando...</p>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Metric icon={Users} label="Total de usuários" value={stats.total_users}
                          sub={`${stats.paid_users ?? 0} associados`}
                          testid="stat-total-users" />
                  <Metric icon={TrendingUp} label="Novos (30d)" value={stats.new_users_last_30d}
                          sub={`${stats.removed_last_30d ?? 0} removidos (30d)`}
                          testid="stat-new-30d" />
                  <Metric icon={Wallet} label="Bonificações distribuídas"
                          value={`R$ ${stats.total_bonuses_distributed.toFixed(2)}`}
                          sub={`${stats.total_ledger_entries} lançamentos`} testid="stat-bonuses" />
                  <Metric icon={Sparkles} label="MRR estimada"
                          value={`R$ ${stats.mrr_estimate.toFixed(2)}`}
                          sub="planos pagos ativos" testid="stat-mrr" />
                </div>

                <Card className="p-6 mt-6" data-testid="matrix-overview-card">
                  <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">Matriz</div>
                  <div className="flex flex-wrap items-center gap-6 text-sm">
                    <div>
                      <div className="text-muted-foreground">Posições ocupadas</div>
                      <div className="font-mono text-lg gold-text">
                        {(stats.matrix_positions_filled ?? 0).toLocaleString("pt-BR")}
                        <span className="text-xs text-muted-foreground"> / {(stats.matrix_total_positions ?? 780).toLocaleString("pt-BR")}</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Profundidade</div>
                      <div className="font-mono text-lg gold-text">
                        {stats.matrix_max_depth ?? 4} <span className="text-xs text-muted-foreground">níveis</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Derramamento automático</div>
                      <div className="font-mono text-lg gold-text">Ativo</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Compressão dinâmica</div>
                      <div className="font-mono text-lg gold-text">
                        {(stats.removed_total ?? 0).toLocaleString("pt-BR")} <span className="text-xs text-muted-foreground">aplicações</span>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 mt-6">
                  <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Distribuição por plano</div>
                  <div className="space-y-3">
                    {PLAN_OPTS.map((p) => {
                      const n = stats.by_plan[p.v] || 0;
                      const pct = stats.total_users ? (n / stats.total_users) * 100 : 0;
                      return (
                        <div key={p.v}>
                          <div className="flex justify-between text-sm">
                            <span>{p.l}</span>
                            <span className="font-mono gold-text">{n} ({pct.toFixed(1)}%)</span>
                          </div>
                          <div className="h-2 rounded-full bg-muted overflow-hidden mt-1">
                            <div className="h-full gold-btn" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
                <Card className="p-6 mt-6" data-testid="admin-plans-config">
                  <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">
                    Planos configurados
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {plansCfg.map((p) => (
                      <div key={p.id} className={`p-5 rounded-xl ${p.featured ? "gold-border" : "border border-border"}`} data-testid={`admin-plan-${p.id}`}>
                        <div className="flex items-baseline justify-between">
                          <div className="font-display text-lg font-bold">{p.name}</div>
                          <div className="font-mono gold-text">R$ {p.price}</div>
                        </div>
                        <div className="mt-3 text-[11px] uppercase tracking-wider text-muted-foreground">Bônus por nível</div>
                        <div className="mt-1 grid grid-cols-5 gap-1">
                          {p.bonuses.map((b, i) => (
                            <div key={`${p.id}-lvl-${i + 1}`} className="text-center p-1 rounded bg-muted/60">
                              <div className="text-[10px] text-muted-foreground">N{i + 1}</div>
                              <div className="font-mono text-xs gold-text">R${b}</div>
                            </div>
                          ))}
                        </div>
                        <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
                          {p.features.map((f) => <li key={f}>• {f}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                  <p className="mt-4 text-xs text-muted-foreground">
                    ⓘ Regra: comissões ficam como <b className="text-foreground">saldo informativo</b> até o fechamento do mês;
                    no fechamento, membros inativos são ignorados e o valor sobe para o próximo ativo.
                  </p>
                </Card>
              </>
            )}
          </TabsContent>

          <TabsContent value="movements" className="mt-6">
            <Card className="p-6">
              <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Movimentações da matriz (últimas 30)</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-ledger-table">
                  <thead className="text-xs uppercase text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="text-left py-2">Data</th>
                      <th className="text-left">Upline</th>
                      <th className="text-left">Entrou</th>
                      <th className="text-left">Plano</th>
                      <th className="text-left">Nível</th>
                      <th className="text-right">Bônus</th>
                      <th className="text-left">Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.length === 0 && (
                      <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Sem movimentações.</td></tr>
                    )}
                    {ledger.map((e) => (
                      <tr key={e.id} className="border-b border-border/50">
                        <td className="py-2 text-muted-foreground">{new Date(e.created_at).toLocaleDateString("pt-BR")}</td>
                        <td>{e.upline.name}</td>
                        <td>{e.downline.name}</td>
                        <td><Badge variant="outline">{e.from_user_plan === "plan_associado" ? "Associado" : "-"}</Badge></td>
                        <td>N{e.level}</td>
                        <td className="text-right">
                          {e.amount > 0
                            ? <span className="font-mono gold-text">R$ {e.amount.toFixed(2)}</span>
                            : <span className="text-xs text-muted-foreground">R$ 0,00</span>}
                        </td>
                        <td className="text-xs text-muted-foreground max-w-[240px]">{e.reason || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="closings" className="mt-6">
            <AdminClosings />
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <AdminOrders />
          </TabsContent>

          <TabsContent value="partners" className="mt-6">
            <AdminPartnerCompanies />
          </TabsContent>

          <TabsContent value="transfers" className="mt-6">
            <AdminTransfers />
          </TabsContent>

          <TabsContent value="withdrawals" className="mt-6">
            <AdminWithdrawals />
          </TabsContent>

          <TabsContent value="benefits" className="mt-6">
            <AdminBenefits />
          </TabsContent>

          <TabsContent value="expired" className="mt-6">
            <ExpiredAccounts />
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <Card className="p-6">
              <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">Configurações do sistema</div>
              <div className="p-4 rounded-xl border border-border" data-testid="inactivity-rules-info">
                <div className="font-medium">Regras de adesão e inatividade</div>
                <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                  Cadastros que não pagarem a adesão de R$ 39,90 em 7 dias são apagados automaticamente. Planos sem
                  renovação em 30 dias ficam inativos; com 2 meses de inatividade aparecem destacados na aba "Vencidas"
                  e só são excluídos com a sua confirmação.
                </p>
              </div>

              <div className="mt-4 p-4 rounded-xl border border-border" data-testid="withdrawal-settings">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-medium">Saques via Pix</div>
                    <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                      Habilita a solicitação de saque para os usuários com plano pago e conta Pix confirmada.
                      Defina a data em que a janela de saque será liberada.
                    </p>
                  </div>
                  <Button
                    variant={settings.withdrawals_enabled ? "default" : "outline"}
                    className={settings.withdrawals_enabled ? "gold-btn hover:opacity-95" : ""}
                    onClick={async () => {
                      const next = !settings.withdrawals_enabled;
                      try {
                        const { data } = await api.patch("/admin/settings", { withdrawals_enabled: next });
                        setSettings((s) => ({ ...s, ...data }));
                        toast.success(next ? "Saques liberados" : "Saques bloqueados");
                      } catch (e) { toast.error(formatApiError(e)); }
                    }}
                    data-testid="toggle-withdrawals"
                  >
                    {settings.withdrawals_enabled ? "Liberado" : "Bloqueado"}
                  </Button>
                </div>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <Label htmlFor="wd-date" className="text-xs uppercase tracking-wider text-muted-foreground">
                    Data de liberação
                  </Label>
                  <Input
                    id="wd-date"
                    type="date"
                    value={settings.withdrawals_open_date ? settings.withdrawals_open_date.slice(0, 10) : ""}
                    onChange={async (e) => {
                      try {
                        const val = e.target.value ? new Date(e.target.value).toISOString() : null;
                        const { data } = await api.patch("/admin/settings", { withdrawals_open_date: val });
                        setSettings((s) => ({ ...s, ...data }));
                        toast.success("Data atualizada");
                      } catch (err) { toast.error(formatApiError(err)); }
                    }}
                    className="w-56"
                    data-testid="withdrawals-open-date"
                  />
                </div>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <Label htmlFor="wd-fee" className="text-xs uppercase tracking-wider text-muted-foreground">
                    Taxa de saque (%)
                  </Label>
                  <Input
                    id="wd-fee"
                    type="number" min="0" max="50" step="0.1"
                    key={settings.withdrawal_fee_percent}
                    defaultValue={settings.withdrawal_fee_percent ?? 2}
                    onBlur={async (e) => {
                      const val = Number(e.target.value);
                      if (val === settings.withdrawal_fee_percent) return;
                      try {
                        const { data } = await api.patch("/admin/settings", { withdrawal_fee_percent: val });
                        setSettings((s) => ({ ...s, ...data }));
                        toast.success("Taxa de saque atualizada");
                      } catch (err) { toast.error(formatApiError(err)); }
                    }}
                    className="w-28"
                    data-testid="withdrawal-fee-input"
                  />
                </div>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <Label htmlFor="wd-min" className="text-xs uppercase tracking-wider text-muted-foreground">
                    Valor mínimo de saque (R$)
                  </Label>
                  <Input
                    id="wd-min"
                    type="number" min="1" step="0.01"
                    key={`wm-${settings.withdrawal_min}`}
                    defaultValue={settings.withdrawal_min ?? 50}
                    onBlur={async (e) => {
                      const val = Number(e.target.value);
                      if (val === settings.withdrawal_min) return;
                      if (!(val >= 1)) { toast.error("Informe um valor mínimo de pelo menos R$ 1,00"); e.target.value = settings.withdrawal_min ?? 50; return; }
                      try {
                        const { data } = await api.patch("/admin/settings", { withdrawal_min: Math.round(val * 100) / 100 });
                        setSettings((s) => ({ ...s, ...data }));
                        toast.success(`Saque mínimo atualizado para ${val.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}`);
                      } catch (err) { toast.error(formatApiError(err)); }
                    }}
                    className="w-32"
                    data-testid="withdrawal-min-input"
                  />
                  <span className="text-xs text-muted-foreground">Vale imediatamente para todos os usuários.</span>
                </div>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <Label htmlFor="partner-commission" className="text-xs uppercase tracking-wider text-muted-foreground">
                    Comissão sobre vendas de parceiros (%)
                  </Label>
                  <Input
                    id="partner-commission"
                    type="number" min="0" max="90" step="0.5"
                    key={`pc-${settings.partner_commission_percent}`}
                    defaultValue={settings.partner_commission_percent ?? 10}
                    onBlur={async (e) => {
                      const val = Number(e.target.value);
                      if (val === settings.partner_commission_percent) return;
                      try {
                        const { data } = await api.patch("/admin/settings", { partner_commission_percent: val });
                        setSettings((s) => ({ ...s, ...data }));
                        toast.success("Comissão de parceiros atualizada");
                      } catch (err) { toast.error(formatApiError(err)); }
                    }}
                    className="w-28"
                    data-testid="partner-commission-input"
                  />
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between p-4 rounded-xl border border-border">
                <div>
                  <div className="font-medium">Alterar senha do admin</div>
                  <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                    A senha é armazenada de forma persistente no banco de dados e nunca é sobrescrita ao reiniciar o servidor.
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setPwdOpen(true)}
                  data-testid="open-change-password"
                >
                  <KeyRound className="h-4 w-4 mr-2" /> Alterar senha
                </Button>
              </div>

              <div className="mt-4 flex items-center justify-between p-4 rounded-xl border border-rose-500/30 bg-rose-500/5">
                <div>
                  <div className="font-medium text-rose-500">Zerar cadastros de teste</div>
                  <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                    Remove todos os usuários (exceto o administrador), todos os lançamentos do ledger,
                    solicitações e tentativas de login. <b>Ação irreversível.</b>
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="border-rose-500 text-rose-500 hover:bg-rose-500/10"
                  onClick={() => setResetOpen(true)}
                  data-testid="open-reset-dialog"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Zerar cadastros
                </Button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <Card className="p-6">
              <div className="inline-flex rounded-lg border border-border p-1 mb-4" role="tablist">
                {[["associate", "Associados"], ["buyer", "Compradores"]].map(([v, l]) => (
                  <button key={v} type="button" role="tab" aria-selected={accountType === v} onClick={() => setAccountType(v)}
                          className={`px-4 py-1.5 rounded-md text-sm transition-colors ${accountType === v ? "gold-btn" : "text-muted-foreground hover:text-foreground"}`}
                          data-testid={`admin-users-type-${v}`}>{l}</button>
                ))}
              </div>
              <div className="flex flex-wrap gap-3 items-end mb-4">
                <div className="flex-1 min-w-[220px]">
                  <div className="text-xs uppercase text-muted-foreground mb-1">Buscar</div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Nome, e-mail, CPF, código..." value={q}
                           onChange={(e) => setQ(e.target.value)}
                           className="pl-9" data-testid="admin-search" />
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase text-muted-foreground mb-1">Plano</div>
                  <Select value={plan} onValueChange={setPlan}>
                    <SelectTrigger className="w-40" data-testid="admin-filter-plan"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {PLAN_OPTS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-xs uppercase text-muted-foreground mb-1">Status</div>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="w-36" data-testid="admin-filter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="active">Ativos</SelectItem>
                      <SelectItem value="inactive">Inativos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground ml-auto">
                  {total} usuário{total === 1 ? "" : "s"}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="text-left py-2">Nome</th>
                      <th className="text-left">Contato</th>
                      <th className="text-left">Plano</th>
                      <th className="text-left">Status</th>
                      <th className="text-left">Ativação</th>
                      <th className="text-right">Saldo</th>
                      <th className="text-right">Saldo Consumo</th>
                      <th className="text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody data-testid="admin-users-table">
                    {loading && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Carregando...</td></tr>}
                    {!loading && users.length === 0 && (
                      <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Nenhum usuário encontrado.</td></tr>
                    )}
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-border/50">
                        <td className="py-2">
                          <button
                            className="font-medium text-left hover:gold-text hover:underline transition-colors flex items-center gap-1"
                            onClick={() => impersonate(u)}
                            title="Entrar nesta conta"
                            data-testid={`impersonate-${u.id}`}
                          >
                            <LogIn className="h-3 w-3 opacity-60" />
                            {u.name}
                          </button>
                          <div className="text-[11px] text-muted-foreground font-mono">{u.referral_code}{u.role === "admin" ? " · admin" : ""}</div>
                        </td>
                        <td className="text-muted-foreground">
                          <div className="text-xs">{u.email}</div>
                          <div className="text-[11px] font-mono">{u.cpf}</div>
                        </td>
                        <td>
                          {u.account_type === "buyer" ? (
                            <Badge variant="outline" className="text-sky-500 border-sky-500/50" data-testid={`user-buyer-badge-${u.id}`}>
                              {u.buyer_restricted ? "Comprador (restrito)" : "Comprador"}
                            </Badge>
                          ) : <Badge variant="outline">{u.plan_label}</Badge>}
                        </td>
                        <td>
                          <button
                            className={u.status === "active" ? "text-emerald-500" : "text-rose-500"}
                            onClick={() => toggleStatus(u)}
                            data-testid={`toggle-status-${u.id}`}
                          >
                            ● {u.status === "active" ? "Ativo" : "Inativo"}
                          </button>
                        </td>
                        <td>
                          {u.activation_status === "approved" && !u.upgrade_status && (
                            <Badge variant="outline" className="text-emerald-500 border-emerald-500/50">Aprovado</Badge>
                          )}
                          {u.activation_status === "pending_review" && (
                            <Badge className="gold-btn">Cadastro em análise</Badge>
                          )}
                          {u.account_type === "buyer" && u.buyer_deadline && (
                            <span className="text-[11px] text-muted-foreground">
                              {u.buyer_restricted ? "Prazo de 2 meses encerrado" : `Pode virar associado até ${new Date(u.buyer_deadline).toLocaleDateString("pt-BR")}`}
                            </span>
                          )}
                          {u.activation_status === "awaiting_payment" && (
                            <Badge variant="outline" className="text-amber-500 border-amber-500/50">Cadastro sem comprovante</Badge>
                          )}
                          {u.upgrade_status === "pending_review" && (
                            <Badge className="gold-btn">Upgrade em análise → {u.requested_plan_label}</Badge>
                          )}
                          {u.upgrade_status === "awaiting_payment" && (
                            <Badge variant="outline" className="text-amber-500 border-amber-500/50">Aguardando PIX → {u.requested_plan_label}</Badge>
                          )}
                        </td>
                        <td className="text-right font-mono gold-text whitespace-nowrap">{(u.balance || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</td>
                        <td className="text-right font-mono text-emerald-500" data-testid={`user-consumption-${u.id}`}>{(u.consumption_balance || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</td>
                        <td className="text-right space-x-1">
                          {u.has_payment_proof && (
                            <Button size="sm" variant="outline" onClick={() => viewProof(u)}
                                    data-testid={`view-proof-${u.id}`}>
                              <FileText className="h-3 w-3 mr-1" />
                              Ver comprovante ({u.proof_type === "upgrade" ? "Upgrade" : "Cadastro"})
                            </Button>
                          )}
                          {(u.activation_status === "pending_review" || u.upgrade_status === "pending_review") && (
                            <Button size="sm" className="gold-btn hover:opacity-95"
                                    onClick={() => activateUser(u)} data-testid={`activate-${u.id}`}>
                              <ShieldCheck className="h-3 w-3 mr-1" /> Ativar
                            </Button>
                          )}
                          {u.role !== "admin" && (
                            <Button size="sm" variant="outline" onClick={() => setManualUser(u)}
                                    data-testid={`manual-activate-${u.id}`}>
                              <Sparkles className="h-3 w-3 mr-1" /> Ativar plano
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => openEdit(u)}
                                  data-testid={`edit-user-${u.id}`}>
                            <Pencil className="h-3 w-3 mr-1" /> Editar
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <ManualActivateDialog user={manualUser} onClose={() => setManualUser(null)}
                            onDone={() => { loadUsers(); loadStats(); }} />

      <Dialog open={pwdOpen} onOpenChange={(o) => { setPwdOpen(o); if (!o) setPwd({ current_password: "", new_password: "", confirm: "" }); }}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Alterar senha do admin</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <PasswordInput
              placeholder="Senha atual"
              value={pwd.current_password}
              onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
              data-testid="pwd-current"
            />
            <PasswordInput
              placeholder="Nova senha (mín. 6 caracteres)"
              value={pwd.new_password}
              onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
              data-testid="pwd-new"
            />
            <PasswordInput
              placeholder="Confirmar nova senha"
              value={pwd.confirm}
              onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
              data-testid="pwd-confirm"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdOpen(false)}>Cancelar</Button>
            <Button
              className="gold-btn hover:opacity-95"
              onClick={changePassword}
              disabled={pwdLoading}
              data-testid="pwd-submit"
            >
              {pwdLoading ? "Salvando..." : "Salvar nova senha"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={(o) => { setResetOpen(o); if (!o) setResetConfirm(""); }}>
        <DialogContent data-testid="reset-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-500">Zerar cadastros de teste</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Isso vai remover <b className="text-foreground">todos os usuários</b> (exceto o administrador),
            <b className="text-foreground"> todos os lançamentos</b> do ledger e zerar o saldo do admin.
            Digite <b className="text-foreground">ZERAR</b> para confirmar.
          </p>
          <Input
            value={resetConfirm}
            onChange={(e) => setResetConfirm(e.target.value)}
            placeholder='Digite "ZERAR"'
            data-testid="reset-confirm-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>Cancelar</Button>
            <Button
              variant="outline"
              className="border-rose-500 text-rose-500 hover:bg-rose-500/10"
              onClick={doReset}
              disabled={resetting || resetConfirm !== "ZERAR"}
              data-testid="reset-confirm-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {resetting ? "Zerando..." : "Confirmar zerar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!proof} onOpenChange={(o) => !o && setProof(null)}>
        <DialogContent className="max-w-3xl" data-testid="proof-dialog">
          <DialogHeader>
            <DialogTitle>Comprovante — {proof?.user?.name}</DialogTitle>
          </DialogHeader>
          {proof && (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                Enviado em {proof.uploaded_at ? new Date(proof.uploaded_at).toLocaleString("pt-BR") : "-"} · {proof.filename}
              </div>
              <div className="text-xs">
                Tipo: <b className="text-foreground">{proof.user?.proof_type === "upgrade" ? "Upgrade para " + (proof.user.requested_plan_label || "-") : "Cadastro (" + (proof.user?.plan_label || "-") + ")"}</b>
              </div>
              {proof.content_type?.startsWith("image/") ? (
                <img
                  src={`data:${proof.content_type};base64,${proof.data_b64}`}
                  alt="Comprovante" className="w-full rounded-xl max-h-[70vh] object-contain bg-black/40"
                  data-testid="proof-image"
                />
              ) : (
                <iframe
                  title="Comprovante"
                  src={`data:${proof.content_type};base64,${proof.data_b64}`}
                  className="w-full h-[70vh] rounded-xl bg-white"
                  data-testid="proof-iframe"
                />
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setProof(null)}>Fechar</Button>
            {proof && proof.user?.activation_status !== "approved" && (
              <Button className="gold-btn hover:opacity-95"
                      onClick={() => activateUser(proof.user)} data-testid="proof-activate-btn">
                <ShieldCheck className="h-4 w-4 mr-2" /> Ativar cadastro
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Editar usuário</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <div className="text-xs text-muted-foreground">Usuário</div>
                <div className="font-medium">{editing.name}</div>
                <div className="text-xs text-muted-foreground">{editing.email}</div>
                <div className="text-xs mt-1">Saldo para saque: <b className="font-mono">R$ {editing.balance.toFixed(2)}</b></div>
              </div>
              <AdminConsumptionPanel userId={editing.id} onChanged={loadUsers} />
              <div>
                <div className="text-xs uppercase text-muted-foreground mb-1">Plano</div>
                <Select value={form.plan} onValueChange={(v) => setForm((f) => ({ ...f, plan: v }))}>
                  <SelectTrigger data-testid="edit-plan"><SelectValue placeholder="Selecione o plano" /></SelectTrigger>
                  <SelectContent>
                    {EDIT_PLAN_OPTS.map((p) => <SelectItem key={p.v} value={p.v} data-testid={`edit-plan-${p.v}`}>{p.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs uppercase text-muted-foreground mb-1">Status</div>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="inactive">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs uppercase text-muted-foreground mb-1">Papel</div>
                <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger data-testid="edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Usuário</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="outline" className="text-rose-500 border-rose-500/40" onClick={() => setDeleteOpen(true)} data-testid="edit-delete-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Excluir usuário
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button className="gold-btn hover:opacity-95" onClick={save} data-testid="edit-save">Salvar</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={deleteOpen} onOpenChange={setDeleteOpen} testid="delete-user-dialog"
        title="Excluir usuário?"
        description={editing ? `${editing.name} será removido definitivamente. Os indicados dele sobem para o patrocinador acima na matriz.` : ""}
        onConfirm={deleteUser} />
    </div>
  );
}
